import { useState, type KeyboardEvent } from "react";
import { sendRecoveryCode } from "../services/cognito";
import {
  BackIcon,
  KeyIcon,
  EnvelopeIcon
} from "../../../shared/icons";

interface RecoverModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function RecoverModal({ isOpen, onClose }: RecoverModalProps) {
  const [step, setStep] = useState<"EMAIL" | "SUCCESS">("EMAIL");
  const [recoverInput, setRecoverInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const handleSendCode = async () => {
    if (!isValidEmail(recoverInput)) return;
    setIsLoading(true);
    setErrorMessage("");
    try {
      await sendRecoveryCode(recoverInput.trim());
      setStep("SUCCESS");
    } catch (err: any) {
      console.error("Forgot password error:", err);
      const message =
        err?.code === "UserNotFoundException"
          ? "El email ingresado no está registrado"
          : err?.code === "InvalidParameterException"
          ? "El formato del email es incorrecto"
          : err?.message || "Error al enviar las instrucciones de recuperación";
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setRecoverInput("");
    setStep("EMAIL");
    setErrorMessage("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="sheet-modal full-screen show" onClick={handleClose}>
      <div className="sheet-content" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-header">
          {step === "EMAIL" && (
            <button 
              className="sheet-back" 
              onClick={handleClose} 
              aria-label="Volver"
            >
              <BackIcon />
            </button>
          )}
          <div className="sheet-title">Recuperar contraseña</div>
        </div>
        
        <div className="recover-content" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 80px)', paddingBottom: 40 }}>
          {step === "EMAIL" ? (
            <>
              <div className="recover-icon-wrap">
                <KeyIcon />
              </div>
              
              <div className="recover-title">¿Olvidaste tu contraseña?</div>
              
              <div className="recover-text">
                Ingresá tu email registrado y te enviaremos un correo electrónico con las instrucciones para restablecer tu acceso.
              </div>

              {errorMessage && (
                <div className="login-error" style={{ marginBottom: 16 }} role="alert">
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span>{errorMessage}</span>
                </div>
              )}

              <div className="login-field">
                <input
                  type="email"
                  className="login-input"
                  placeholder="Tu email registrado"
                  value={recoverInput}
                  onChange={(e) => setRecoverInput(e.target.value)}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && !isLoading && isValidEmail(recoverInput) && handleSendCode()}
                  autoFocus
                  disabled={isLoading}
                />
              </div>
              <button
                className="login-btn"
                style={{ marginTop: 8 }}
                onClick={handleSendCode}
                disabled={!isValidEmail(recoverInput) || isLoading}
              >
                {isLoading ? "Enviando..." : "Enviar instrucciones"}
              </button>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', animation: 'fadeIn 0.3s ease' }}>
              <div className="recover-icon-wrap" style={{ background: 'rgba(0, 104, 55, 0.1)' }}>
                <EnvelopeIcon />
              </div>
              
              <div className="recover-title" style={{ marginTop: 12 }}>¡Instrucciones enviadas!</div>
              
              <div className="recover-text" style={{ marginTop: 10, marginBottom: 28, maxWidth: 360 }}>
                Te enviamos un correo electrónico con una <strong>contraseña temporal</strong>. 
                <br /><br />
                Utilizala para iniciar sesión desde la pantalla principal y el sistema te guiará automáticamente para establecer tu contraseña definitiva.
              </div>

              <button
                className="login-btn"
                onClick={handleClose}
                style={{ maxWidth: 280 }}
              >
                Volver al inicio de sesión
              </button>
            </div>
          )}

          <div style={{ textAlign: "center", marginTop: 32, fontSize: 13, color: "#757575" }}>
            Si tenés inconvenientes, contactá a tu operador comercial al{" "}
            <strong> +54 0358 4933 333</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
